import { useListGraphEndpoints } from '@squareup/dex-feature-hooks-graph';
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import {
  EndpointsWebhookCardList,
  ReferenceDeprecatedIndexPage,
} from '@squareup/dex-tech-ui';
import {
  ReferenceHrefBuilders,
  ReferencePath,
  toUrl,
} from '@squareup/dex-tech-utils-routing';
import { HeadWrapper } from '@squareup/dex-ui';
import {
  getNextPublicApiReferenceNameOrDefault,
  getNextPublicSquareDocsURL,
} from '@squareup/dex-utils-environment';
import {
  filterForDeprecatedGraphObjects,
  addEndpointByApiCategoryToMap,
  getSortedCategoriesEndpointsMap,
} from '@squareup/dex-utils-oas';
import { useParams } from '@squareup/dex-utils-shared-routing-hooks';
import React, { ReactNode, useMemo, useRef, useCallback } from 'react';

import { SquareVersionPage } from '../../../../shared/SquareVersionPage';

const PageContent = () => {
  const { endpoints } = useListGraphEndpoints();
  const filteredEndpoints = filterForDeprecatedGraphObjects(endpoints);
  const deprecatedEndpointsListRef = useRef<HTMLElement | null>(null);
  const query = useParams();

  const [sortedCategoriesEndpointsMap] = useMemo(() => {
    if (!filteredEndpoints) {
      return [];
    }

    const categoriesEndpointsMap = filteredEndpoints.reduce(
      addEndpointByApiCategoryToMap,
      new Map()
    );

    const sortedCategoriesEndpointsMap = getSortedCategoriesEndpointsMap(
      categoriesEndpointsMap
    );

    return [sortedCategoriesEndpointsMap];
  }, [filteredEndpoints]);

  const setEndpointListRef = useCallback((ref: HTMLElement | null) => {
    deprecatedEndpointsListRef.current = ref;
  }, []);

  const aside = useMemo(() => {
    return {
      trackingId: 'square-version-deprecated',
      content: sortedCategoriesEndpointsMap ? (
        <EndpointsWebhookCardList
          categoriesWebhooksMap={sortedCategoriesEndpointsMap}
          setListRef={setEndpointListRef}
        />
      ) : null,
      title: <SpecVersionPickerContainer variant="reference" />,
      navigationItems: [
        {
          trackingId: 'navigate-to-reference-deprecated',
          href: {
            pathname: ReferencePath.VERSION_DEPRECATED,
            query,
          },
          isActive: true,
          testId: 'endpoints',
          text: 'Endpoints',
        },
        {
          trackingId: 'navigate-to-reference-deprecated-webhooks',
          href: {
            pathname: ReferencePath.VERSION_DEPRECATED_WEBHOOKS,
            query,
          },
          isActive: false,
          testId: 'webhooks',
          text: 'Webhooks',
        },
      ],
    };
  }, [setEndpointListRef, sortedCategoriesEndpointsMap, query]);

  const description =
    'View older Connect APIs and endpoints that have been deprecated and replaced with newer versions in the Square API.';
  const title = `Deprecated - ${getNextPublicApiReferenceNameOrDefault()}`;

  return (
    <>
      <HeadWrapper
        description={description}
        preconnectUrl={getNextPublicSquareDocsURL()}
        title={title}
        canonicalPath={toUrl(
          ReferenceHrefBuilders.versionDeprecated({
            square_version: query.square_version as string,
          })
        )}
      />
      <ReferenceDeprecatedIndexPage
        aside={aside}
      ></ReferenceDeprecatedIndexPage>
    </>
  );
};

class Page extends SquareVersionPage {
  public static override routeHandlerName = '[square_version]/deprecated';

  protected override getPageContent(): ReactNode {
    return super.getPageContent({
      children: <PageContent />,
    });
  }
}

export default Page;
